// src/pages/CreatePlace.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  SKIN_THEMES,
} from "../layouts/constants";

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";

import logo from "../assets/image/eblackdog_sentinal.png";

/* =========================================================
   CONSTANTS
========================================================= */

const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const SELECTED_LOCATION_ZOOM = 16;

/* =========================================================
   GOOGLE MAPS LOADER
========================================================= */

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");

  if (errorText.includes("RefererNotAllowedMapError")) {
    return (
      "This website is not authorized for the Google Maps API key. " +
      "Add http://localhost:3000/* and the production frontend domain under the API key website restrictions."
    );
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }

  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google Maps can only be loaded in a browser.")
    );
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (!apiKey) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file."
      )
    );
  }

  if (googleMapsLoadingPromise) {
    return googleMapsLoadingPromise;
  }

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;

    const resolveOnce = () => {
      if (settled) return;
      if (!window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(
        error instanceof Error
          ? error
          : new Error("Google Maps JavaScript API failed to load.")
      );
    };

    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(
        new Error(
          "Google Maps script loaded, but the Maps API is unavailable."
        )
      );
    };

    const handleScriptError = () => {
      rejectOnce(
        new Error("Google Maps JavaScript API network request failed.")
      );
    };

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (callbackError) {
          console.error(
            "Previous Google Maps authentication callback failed:",
            callbackError
          );
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };

    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, {
        once: true,
      });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;

    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places",
      v: "weekly",
    });

    script.src =
      "https://maps.googleapis.com/maps/api/js?" + queryParameters.toString();
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
}

/* =========================================================
   HELPERS
========================================================= */

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((item) => item.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken") ||
    ""
  );
}

function isUserLoggedIn() {
  const loginValue = localStorage.getItem("isLoggedIn");
  return (
    loginValue === "true" || loginValue === "1" || loginValue === "yes"
  );
}

function api(path) {
  const base = String(API_BASE_URL || "").replace(/\/+$/, "");
  const normalizedPath = String(path || "").startsWith("/")
    ? String(path)
    : `/${String(path || "")}`;

  if (base.endsWith("/api") && normalizedPath.startsWith("/api/")) {
    return base + normalizedPath.replace(/^\/api/, "");
  }

  return base + normalizedPath;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (
    provider === "google" ||
    provider === "googlemaps" ||
    provider === "google_maps"
  ) {
    return "google";
  }
  return "openstreetmap";
}

function toFiniteNumber(value) {
  if (value === null || value === undefined) return null;
  const trimmedValue = typeof value === "string" ? value.trim() : value;
  if (trimmedValue === "") return null;
  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function isValidLatitude(value) {
  const latitude = toFiniteNumber(value);
  return latitude !== null && latitude >= -90 && latitude <= 90;
}

function isValidLongitude(value) {
  const longitude = toFiniteNumber(value);
  return longitude !== null && longitude >= -180 && longitude <= 180;
}

function getCurrentOrigin() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

function escapeHtmlAttribute(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function createCustomIcon({
  bg = "#2563eb",
  border = "#ffffff",
  logoUrl = "",
}) {
  const safeBackground = escapeHtmlAttribute(bg);
  const safeBorder = escapeHtmlAttribute(border);
  const safeLogoUrl = escapeHtmlAttribute(logoUrl);

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 42px;
        height: 42px;
        border-radius: 50%;
        background: ${safeBackground};
        border: 3px solid ${safeBorder};
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      ">
        <img
          src="${safeLogoUrl}"
          alt=""
          style="width: 29px; height: 29px; object-fit: contain;"
        />
      </div>
    `,
    iconSize: [42, 42],
    iconAnchor: [21, 42],
    popupAnchor: [0, -35],
  });
}

const routeIcon = createCustomIcon({
  bg: "#2563eb",
  logoUrl: logo,
});

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function CreatePlace() {
  const navigate = useNavigate();
  useSkin();

  /* ---------- STATE ---------- */
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  const [routeName, setRouteName] = useState("");
  const [routeLocation, setRouteLocation] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  /* ---------- LEAFLET REFS ---------- */
  const leafletMapElementRef = useRef(null);
  const leafletMapInstanceRef = useRef(null);
  const leafletMarkerRef = useRef(null);
  const leafletGeocoderRef = useRef(null);
  const leafletClickHandlerRef = useRef(null);
  const leafletMarkerDragHandlerRef = useRef(null);

  /* ---------- GOOGLE REFS ---------- */
  const googleMapElementRef = useRef(null);
  const googleMapInstanceRef = useRef(null);
  const googleMarkerRef = useRef(null);
  const googleGeocoderRef = useRef(null);
  const googleClickListenerRef = useRef(null);
  const googleMarkerDragListenerRef = useRef(null);

  /* ---------- MAP PROVIDER ---------- */
  const mapProvider = useMemo(
    () => normalizeMapProvider(dashboardData.user_map),
    [dashboardData.user_map]
  );
  const isGoogleMap = mapProvider === "google";

  /* ---------- LOCATION UPDATE ---------- */
  const updateSelectedLocation = useCallback(
    (lat, lng, locationName) => {
      const numericLatitude = toFiniteNumber(lat);
      const numericLongitude = toFiniteNumber(lng);

      if (numericLatitude === null || numericLongitude === null) return;

      if (
        !isValidLatitude(numericLatitude) ||
        !isValidLongitude(numericLongitude)
      ) {
        toast.error("Invalid latitude or longitude.");
        return;
      }

      const finalLocation = String(
        locationName || routeLocation || "Selected location"
      ).trim();

      setLatitude(numericLatitude.toFixed(7));
      setLongitude(numericLongitude.toFixed(7));
      setRouteLocation(finalLocation);
      setMapError("");

      /* OpenStreetMap marker */
      if (!isGoogleMap && leafletMapInstanceRef.current) {
        const leafletMap = leafletMapInstanceRef.current;
        const position = [numericLatitude, numericLongitude];

        if (leafletMarkerRef.current) {
          leafletMarkerRef.current.setLatLng(position);
        } else {
          const marker = L.marker(position, {
            icon: routeIcon,
            draggable: true,
          }).addTo(leafletMap);

          const handleDragEnd = (event) => {
            const markerPosition = event.target.getLatLng();
            reverseGeocodeLocation(
              markerPosition.lat,
              markerPosition.lng,
              false
            );
          };

          marker.on("dragend", handleDragEnd);
          leafletMarkerRef.current = marker;
          leafletMarkerDragHandlerRef.current = handleDragEnd;
        }

        leafletMarkerRef.current
          .bindPopup(`📍 ${finalLocation}`)
          .openPopup();

        leafletMap.setView(position, SELECTED_LOCATION_ZOOM);
      }

      /* Google marker */
      if (isGoogleMap && googleMapInstanceRef.current && window.google?.maps) {
        const googleMap = googleMapInstanceRef.current;
        const position = {
          lat: numericLatitude,
          lng: numericLongitude,
        };

        if (googleMarkerRef.current) {
          googleMarkerRef.current.setPosition(position);
        } else {
          const marker = new window.google.maps.Marker({
            map: googleMap,
            position,
            draggable: true,
            title: finalLocation,
          });

          const dragListener = marker.addListener("dragend", (event) => {
            const draggedLat = event.latLng?.lat();
            const draggedLng = event.latLng?.lng();
            if (Number.isFinite(draggedLat) && Number.isFinite(draggedLng)) {
              reverseGeocodeLocation(draggedLat, draggedLng, true);
            }
          });

          googleMarkerRef.current = marker;
          googleMarkerDragListenerRef.current = dragListener;
        }

        googleMarkerRef.current.setTitle(finalLocation);
        googleMap.panTo(position);
        googleMap.setZoom(SELECTED_LOCATION_ZOOM);
      }
    },
    [isGoogleMap, routeLocation]
  );

  /* ---------- REVERSE GEOCODING ---------- */
  const reverseGeocodeLocation = useCallback(
    async (lat, lng, useGoogle = isGoogleMap) => {
      const numericLatitude = toFiniteNumber(lat);
      const numericLongitude = toFiniteNumber(lng);

      if (
        numericLatitude === null ||
        numericLongitude === null ||
        !isValidLatitude(numericLatitude) ||
        !isValidLongitude(numericLongitude)
      ) {
        toast.error("Invalid location coordinates.");
        return;
      }

      if (useGoogle) {
        try {
          if (!googleGeocoderRef.current) {
            throw new Error("Google geocoder is not ready.");
          }

          const response = await googleGeocoderRef.current.geocode({
            location: { lat: numericLatitude, lng: numericLongitude },
          });

          const firstResult = response?.results?.[0];
          const locationName =
            firstResult?.formatted_address || "Selected location";

          updateSelectedLocation(
            numericLatitude,
            numericLongitude,
            locationName
          );

          toast.success("Location pinned successfully.", { id: "map-pin" });
        } catch (error) {
          console.error("Google reverse geocoding error:", error);
          updateSelectedLocation(
            numericLatitude,
            numericLongitude,
            "Selected location"
          );
          toast.error(
            "Address could not be fetched, but the coordinates were selected.",
            { id: "map-pin" }
          );
        }
        return;
      }

      try {
        const reverseUrl =
          "https://nominatim.openstreetmap.org/reverse" +
          "?format=json" +
          `&lat=${encodeURIComponent(numericLatitude)}` +
          `&lon=${encodeURIComponent(numericLongitude)}` +
          "&zoom=18&addressdetails=1";

        const response = await fetch(reverseUrl, {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Reverse geocoding failed: ${response.status}`);
        }

        const data = await response.json();
        const locationName = data?.display_name || "Selected location";

        updateSelectedLocation(
          numericLatitude,
          numericLongitude,
          locationName
        );

        toast.success("Location pinned successfully.", { id: "map-pin" });
      } catch (error) {
        console.error("OpenStreetMap reverse geocoding error:", error);
        updateSelectedLocation(
          numericLatitude,
          numericLongitude,
          "Selected location"
        );
        toast.error(
          "Address could not be fetched, but the coordinates were selected.",
          { id: "map-pin" }
        );
      }
    },
    [isGoogleMap, updateSelectedLocation]
  );

  /* ---------- FETCH TENANT SETTINGS ---------- */
  const fetchDashboardData = useCallback(async (token) => {
    try {
      setLoading(true);

      const response = await fetch(api("/api/dashboard/admin/dashboard"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result?.message || "Unable to load tenant configuration."
        );
      }

      const companyDetails =
        result?.data?.companyDetails || result?.companyDetails || {};

      setDashboardData({
        company_name: companyDetails.company_name || "",
        licensedFor: companyDetails.licensedFor || "",
        admin_active: companyDetails.admin_active || 0,
        company_address: companyDetails.company_address || "",
        company_phone: companyDetails.company_phone || "",
        admin_email: companyDetails.admin_email || "",
        company_logo: companyDetails.company_logo || "",
        user_map: normalizeMapProvider(companyDetails.user_map),
      });
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      setDashboardData((currentValue) => ({
        ...currentValue,
        user_map: "openstreetmap",
      }));
      toast.error(error.message || "Unable to load tenant configuration.");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ---------- LOGIN CHECK ---------- */
  useEffect(() => {
    const token = getToken();
    if (!token || !isUserLoggedIn()) {
      navigate("/", { replace: true });
      return;
    }
    fetchDashboardData(token);
  }, [fetchDashboardData, navigate]);

  /* ---------- DESTROY MAPS ---------- */
  const destroyLeafletMap = useCallback(() => {
    const map = leafletMapInstanceRef.current;
    const marker = leafletMarkerRef.current;

    if (marker && leafletMarkerDragHandlerRef.current) {
      marker.off("dragend", leafletMarkerDragHandlerRef.current);
    }
    if (map && leafletClickHandlerRef.current) {
      map.off("click", leafletClickHandlerRef.current);
    }
    if (map) map.remove();

    leafletMapInstanceRef.current = null;
    leafletMarkerRef.current = null;
    leafletGeocoderRef.current = null;
    leafletClickHandlerRef.current = null;
    leafletMarkerDragHandlerRef.current = null;
  }, []);

  const destroyGoogleMap = useCallback(() => {
    if (googleClickListenerRef.current) {
      googleClickListenerRef.current.remove();
      googleClickListenerRef.current = null;
    }
    if (googleMarkerDragListenerRef.current) {
      googleMarkerDragListenerRef.current.remove();
      googleMarkerDragListenerRef.current = null;
    }
    if (googleMarkerRef.current) {
      googleMarkerRef.current.setMap(null);
      googleMarkerRef.current = null;
    }
    googleMapInstanceRef.current = null;
    googleGeocoderRef.current = null;
  }, []);

  /* ---------- INIT OPENSTREETMAP ---------- */
  useEffect(() => {
    if (
      loading ||
      isGoogleMap ||
      !leafletMapElementRef.current ||
      leafletMapInstanceRef.current
    ) {
      return undefined;
    }

    setMapError("");
    setMapLoading(true);
    destroyGoogleMap();

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    try {
      const map = L.map(leafletMapElementRef.current, {
        zoomControl: true,
        attributionControl: true,
      });

      leafletMapInstanceRef.current = map;
      map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);

      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const geocoder = L.Control.geocoder({
        defaultMarkGeocode: false,
        position: "topleft",
        placeholder: "Search location...",
        errorMessage: "Location not found",
      })
        .on("markgeocode", (event) => {
          const center = event.geocode.center;
          const locationName = event.geocode.name || "Selected location";

          if (event.geocode.bbox) {
            map.fitBounds(event.geocode.bbox);
          } else {
            map.setView(center, SELECTED_LOCATION_ZOOM);
          }

          updateSelectedLocation(center.lat, center.lng, locationName);
          toast.success("Location found and pinned.", {
            id: "location-search",
          });
        })
        .addTo(map);

      leafletGeocoderRef.current = geocoder;

      const handleMapClick = (event) => {
        reverseGeocodeLocation(event.latlng.lat, event.latlng.lng, false);
      };

      map.on("click", handleMapClick);
      leafletClickHandlerRef.current = handleMapClick;

      const resizeMap = () => {
        if (leafletMapInstanceRef.current) {
          leafletMapInstanceRef.current.invalidateSize(true);
        }
      };

      const timers = [
        window.setTimeout(resizeMap, 100),
        window.setTimeout(resizeMap, 500),
        window.setTimeout(resizeMap, 1000),
      ];

      window.addEventListener("resize", resizeMap);
      setMapLoading(false);

      return () => {
        timers.forEach((timer) => window.clearTimeout(timer));
        window.removeEventListener("resize", resizeMap);
        destroyLeafletMap();
      };
    } catch (error) {
      console.error("OpenStreetMap initialization error:", error);
      setMapError(error.message || "Unable to initialize OpenStreetMap.");
      setMapLoading(false);
      return undefined;
    }
  }, [
    destroyGoogleMap,
    destroyLeafletMap,
    isGoogleMap,
    loading,
    reverseGeocodeLocation,
    updateSelectedLocation,
  ]);

  /* ---------- INIT GOOGLE MAP ---------- */
  useEffect(() => {
    if (
      loading ||
      !isGoogleMap ||
      !googleMapElementRef.current ||
      googleMapInstanceRef.current
    ) {
      return undefined;
    }

    let cancelled = false;
    setMapError("");
    setMapLoading(true);
    destroyLeafletMap();

    const initializeGoogleMap = async () => {
      try {
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);

        if (cancelled || !googleMapElementRef.current) return;

        const map = new window.google.maps.Map(googleMapElementRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          clickableIcons: false,
        });

        googleMapInstanceRef.current = map;
        googleGeocoderRef.current = new window.google.maps.Geocoder();

        googleClickListenerRef.current = map.addListener("click", (event) => {
          const selectedLat = event.latLng?.lat();
          const selectedLng = event.latLng?.lng();
          if (Number.isFinite(selectedLat) && Number.isFinite(selectedLng)) {
            reverseGeocodeLocation(selectedLat, selectedLng, true);
          }
        });

        if (isValidLatitude(latitude) && isValidLongitude(longitude)) {
          updateSelectedLocation(
            Number(latitude),
            Number(longitude),
            routeLocation || "Selected location"
          );
        }
      } catch (error) {
        console.error("Google Maps initialization error:", error);
        const message = getGoogleMapsErrorMessage(error);
        setMapError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    };

    initializeGoogleMap();

    return () => {
      cancelled = true;
      destroyGoogleMap();
    };
  }, [
    destroyGoogleMap,
    destroyLeafletMap,
    isGoogleMap,
    latitude,
    loading,
    longitude,
    reverseGeocodeLocation,
    routeLocation,
    updateSelectedLocation,
  ]);

  /* ---------- SEARCH LOCATION ---------- */
  const handleSearchLocation = useCallback(async () => {
    const query = routeLocation.trim();
    if (!query) {
      toast.error("Enter a location name first.");
      return;
    }

    toast.loading("Searching location...", { id: "location-search" });

    if (isGoogleMap) {
      try {
        if (!googleGeocoderRef.current) {
          throw new Error("Google Maps is still loading.");
        }

        const response = await googleGeocoderRef.current.geocode({
          address: query,
        });

        const firstResult = response?.results?.[0];
        const position = firstResult?.geometry?.location;

        if (!position) throw new Error("Location not found.");

        const lat = position.lat();
        const lng = position.lng();
        const locationName = firstResult?.formatted_address || query;

        updateSelectedLocation(lat, lng, locationName);
        toast.success("Location found and pinned.", { id: "location-search" });
      } catch (error) {
        console.error("Google location search error:", error);
        toast.error(error.message || "Unable to search location.", {
          id: "location-search",
        });
      }
      return;
    }

    try {
      const searchUrl =
        "https://nominatim.openstreetmap.org/search" +
        "?format=json" +
        `&q=${encodeURIComponent(query)}` +
        "&limit=1&addressdetails=1";

      const response = await fetch(searchUrl, {
        headers: { Accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`Location search failed: ${response.status}`);
      }

      const results = await response.json();

      if (!Array.isArray(results) || results.length === 0) {
        throw new Error(
          "Location not found. Try a more specific place name."
        );
      }

      const place = results[0];
      const lat = Number(place.lat);
      const lng = Number(place.lon);

      if (!isValidLatitude(lat) || !isValidLongitude(lng)) {
        throw new Error("The search returned invalid coordinates.");
      }

      const locationName = place.display_name || query;
      updateSelectedLocation(lat, lng, locationName);
      toast.success("Location found and pinned.", { id: "location-search" });
    } catch (error) {
      console.error("OpenStreetMap location search error:", error);
      toast.error(error.message || "Unable to search location.", {
        id: "location-search",
      });
    }
  }, [isGoogleMap, routeLocation, updateSelectedLocation]);

  /* ---------- USE CURRENT LOCATION ---------- */
  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Current location is not supported by this browser.");
      return;
    }

    toast.loading("Getting current location...", { id: "current-location" });

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        reverseGeocodeLocation(lat, lng, isGoogleMap);
        toast.success("Current location selected.", {
          id: "current-location",
        });
      },
      (error) => {
        console.error("Current location error:", error);
        let message = "Unable to get your current location.";
        if (error.code === error.PERMISSION_DENIED) {
          message =
            "Location permission was denied. Allow location access in your browser.";
        }
        if (error.code === error.POSITION_UNAVAILABLE) {
          message = "Your current location is unavailable.";
        }
        if (error.code === error.TIMEOUT) {
          message = "Getting the current location timed out.";
        }
        toast.error(message, { id: "current-location" });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  }, [isGoogleMap, reverseGeocodeLocation]);

  /* ---------- CLEAR LOCATION ---------- */
  const handleClearLocation = useCallback(() => {
    setRouteLocation("");
    setLatitude("");
    setLongitude("");
    setMapError("");

    if (leafletMarkerRef.current) {
      leafletMarkerRef.current.remove();
      leafletMarkerRef.current = null;
    }
    if (leafletMapInstanceRef.current) {
      leafletMapInstanceRef.current.setView(
        [DEFAULT_CENTER.lat, DEFAULT_CENTER.lng],
        DEFAULT_ZOOM
      );
    }

    if (googleMarkerDragListenerRef.current) {
      googleMarkerDragListenerRef.current.remove();
      googleMarkerDragListenerRef.current = null;
    }
    if (googleMarkerRef.current) {
      googleMarkerRef.current.setMap(null);
      googleMarkerRef.current = null;
    }
    if (googleMapInstanceRef.current) {
      googleMapInstanceRef.current.setCenter(DEFAULT_CENTER);
      googleMapInstanceRef.current.setZoom(DEFAULT_ZOOM);
    }

    toast.success("Selected location cleared.");
  }, []);

  /* ---------- VALIDATION ---------- */
  const validatePlaceForm = useCallback(() => {
    const trimmedName = routeName.trim();
    const trimmedLocation = routeLocation.trim();

    if (!trimmedName) {
      toast.error("Please enter a place name.");
      return null;
    }
    if (trimmedName.length < 2) {
      toast.error("Place name must contain at least 2 characters.");
      return null;
    }
    if (trimmedName.length > 150) {
      toast.error("Place name cannot exceed 150 characters.");
      return null;
    }
    if (!trimmedLocation) {
      toast.error("Please select a location.");
      return null;
    }
    if (!latitude || !longitude) {
      toast.error("Please search for a location or pin one on the map.");
      return null;
    }

    const parsedLatitude = Number(latitude);
    const parsedLongitude = Number(longitude);

    if (!isValidLatitude(parsedLatitude)) {
      toast.error("Latitude must be between -90 and 90.");
      return null;
    }
    if (!isValidLongitude(parsedLongitude)) {
      toast.error("Longitude must be between -180 and 180.");
      return null;
    }

    return {
      route_name: trimmedName,
      location: trimmedLocation,
      latitude: parsedLatitude,
      longitude: parsedLongitude,
    };
  }, [latitude, longitude, routeLocation, routeName]);

  /* ---------- SUBMIT ---------- */
  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (saving) return;

      const payload = validatePlaceForm();
      if (!payload) return;

      const token = getToken();
      if (!token) {
        toast.error("Your session has expired. Please log in again.");
        navigate("/", { replace: true });
        return;
      }

      try {
        setSaving(true);
        toast.loading("Creating place...", { id: "create-place" });

        const response = await fetch(api("/api/routes"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => ({}));

        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("authToken");
          localStorage.removeItem("adminToken");
          localStorage.removeItem("isLoggedIn");
          throw new Error("Your session has expired. Please log in again.");
        }

        if (!response.ok || result.success === false) {
          throw new Error(
            result.message || result.error || "Failed to create place."
          );
        }

        toast.success(result.message || "Place created successfully!", {
          id: "create-place",
        });

        setRouteName("");
        setRouteLocation("");
        setLatitude("");
        setLongitude("");

        window.setTimeout(() => {
          navigate("/place_list", { replace: true });
        }, 900);
      } catch (error) {
        console.error("Create place error:", error);
        toast.error(error.message || "Failed to create place.", {
          id: "create-place",
        });

        if (
          String(error.message || "").includes("session has expired")
        ) {
          window.setTimeout(() => {
            navigate("/", { replace: true });
          }, 700);
        }
      } finally {
        setSaving(false);
      }
    },
    [navigate, saving, validatePlaceForm]
  );

  /* ---------- KEYBOARD SEARCH ---------- */
  const handleLocationKeyDown = useCallback(
    (event) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        event.preventDefault();
        handleSearchLocation();
      }
    },
    [handleSearchLocation]
  );

  /* ---------- DERIVED ---------- */
  const routePage = useMemo(
    () => (isGoogleMap ? "/route-master" : "/route-master1"),
    [isGoogleMap]
  );

  const selectedCoordinateLabel = useMemo(() => {
    if (!latitude || !longitude) return "No location selected";
    return `${latitude}, ${longitude}`;
  }, [latitude, longitude]);

  const googleMapSetupMessage = useMemo(() => {
    if (!isGoogleMap) return "";
    const origin = getCurrentOrigin();
    if (!origin) {
      return "Configure this frontend domain in the Google Maps API key restrictions.";
    }
    return `Authorize ${origin}/* in the Google Maps API key website restrictions.`;
  }, [isGoogleMap]);

  /* ---------- LOADING ---------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-4 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)]">
          Loading place management...
        </p>
      </div>
    );
  }

  /* =======================================================
     RENDER — Route Master style
  ======================================================= */

  return (
    <Layout routePage={routePage}>
      <Toaster position="top-right" />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        {/* Page Title */}
        <h1 className="text-[22px] font-extrabold tracking-tight m-0">
          Place Management
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Create a new place and pin its exact location on the map
        </p>

        {/* Header Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[var(--surface-border)] bg-white p-1.5">
                <img
                  src={dashboardData.company_logo || logo}
                  alt={dashboardData.company_name || "eBLACKDOG Sentinel"}
                  className="h-full w-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = logo;
                  }}
                />
              </div>
              <div>
                <h2 className="text-[18px] font-extrabold m-0">Create Place</h2>
                <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                  Add a place name and select coordinates from the map
                </p>
              </div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate("/place_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition hover:opacity-90 hover:-translate-y-0.5"
              >
                Place List
              </button>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-[18px]">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] m-0">
              Company
            </p>
            <p className="mt-2 text-[13px] font-extrabold text-[var(--text-primary)] truncate m-0">
              {dashboardData.company_name || "Not available"}
            </p>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] m-0">
              Licensed For
            </p>
            <p className="mt-2 text-[13px] font-extrabold text-[var(--text-primary)] truncate m-0">
              {dashboardData.licensedFor || "Not available"}
            </p>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] m-0">
              Map Provider
            </p>
            <p className="mt-2 text-[13px] font-extrabold text-[var(--text-primary)] capitalize m-0">
              {isGoogleMap ? "Google Maps" : "OpenStreetMap"}
            </p>
          </div>

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-5 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <p className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] m-0">
              Selected Coordinates
            </p>
            <p className="mt-2 text-[13px] font-extrabold text-[var(--text-primary)] truncate m-0">
              {selectedCoordinateLabel}
            </p>
          </div>
        </div>

        {/* Form + Map Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-[18px]">
          {/* LEFT — Form Card */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <div className="mb-5">
              <h3 className="text-[18px] font-extrabold m-0">Place Information</h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Enter the place name and select its exact map location
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Place Name */}
              <div className="mb-4">
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Place Name <span className="text-[var(--red)]">*</span>
                </label>
                <input
                  id="routeName"
                  type="text"
                  value={routeName}
                  onChange={(e) => setRouteName(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  maxLength={150}
                  autoComplete="off"
                  placeholder="Example: Main Office"
                  disabled={saving}
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)] disabled:opacity-60 disabled:cursor-not-allowed"
                />
                <div className="mt-1 flex justify-end">
                  <span className="text-[11px] text-[var(--text-muted)]">
                    {routeName.length}/150
                  </span>
                </div>
              </div>

              {/* Location Search */}
              <div className="mb-4">
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Location <span className="text-[var(--red)]">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="routeLocation"
                    type="text"
                    value={routeLocation}
                    onChange={(e) => setRouteLocation(e.target.value)}
                    onKeyDown={handleLocationKeyDown}
                    autoComplete="off"
                    placeholder="Search city, street or address"
                    disabled={saving || mapLoading}
                    className="min-w-0 flex-1 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)] disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  <button
                    type="button"
                    onClick={handleSearchLocation}
                    disabled={saving || mapLoading || !routeLocation.trim()}
                    className="shrink-0 border-0 rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer bg-[var(--btn-bg)] text-[var(--btn-text)] shadow-[0_4px_18px_var(--accent-glow)] transition hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    Search
                  </button>
                </div>
                <p className="mt-2 text-[12px] text-[var(--text-muted)] m-0">
                  You can also click on the map or drag the marker.
                </p>
              </div>

              {/* Coordinates */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Latitude
                  </label>
                  <input
                    type="text"
                    value={latitude}
                    readOnly
                    placeholder="Not selected"
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Longitude
                  </label>
                  <input
                    type="text"
                    value={longitude}
                    readOnly
                    placeholder="Not selected"
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                  />
                </div>
              </div>

              {/* Location Actions */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={saving || mapLoading}
                  className="border-0 rounded-xl px-4 py-2.5 text-[12px] font-extrabold cursor-pointer bg-[var(--btn-bg)] text-[var(--btn-text)] hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Use Current Location
                </button>
                <button
                  type="button"
                  onClick={handleClearLocation}
                  disabled={
                    saving || (!latitude && !longitude && !routeLocation)
                  }
                  className="border-0 rounded-xl px-4 py-2.5 text-[12px] font-extrabold cursor-pointer bg-[var(--red)] text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear Location
                </button>
              </div>

              {/* Selected Location Card */}
              <div className="border border-[var(--surface-border)] rounded-2xl p-4 bg-[var(--surface)] mb-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--accent-faint)] text-lg">
                    📍
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] m-0">
                      Selected Location
                    </p>
                    <p className="mt-1 break-words text-[13px] font-bold leading-6 text-[var(--text-primary)] m-0">
                      {routeLocation || "No location selected"}
                    </p>
                    {latitude && longitude && (
                      <p className="mt-1 text-[12px] font-semibold text-[var(--text-muted)] m-0">
                        {latitude}, {longitude}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Google Setup Warning */}
              {isGoogleMap && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-4">
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-amber-700 m-0">
                    Google Maps setup
                  </p>
                  <p className="mt-1 text-[12px] font-semibold leading-5 text-amber-700 m-0">
                    {googleMapSetupMessage}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => navigate("/place_list")}
                  disabled={saving}
                  className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || mapLoading}
                  className="border-0 rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer bg-[var(--btn-bg)] text-[var(--btn-text)] shadow-[0_4px_18px_var(--accent-glow)] transition hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 inline-flex items-center justify-center gap-2"
                >
                  {saving && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {saving ? "Creating..." : "Create Place"}
                </button>
              </div>
            </form>
          </div>

          {/* RIGHT — Map Card */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <div className="mb-3.5 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h3 className="text-[18px] font-extrabold m-0">
                  Pin Place Location
                </h3>
                <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                  Search, click, or drag the marker to set exact coordinates
                </p>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-[var(--surface-border)] bg-[var(--surface)] px-3 py-2">
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    mapError
                      ? "bg-red-500"
                      : mapLoading
                        ? "animate-pulse bg-amber-500"
                        : "bg-emerald-500"
                  }`}
                />
                <span className="text-[12px] font-extrabold text-[var(--text-primary)]">
                  {mapError
                    ? "Map Error"
                    : mapLoading
                      ? "Loading Map"
                      : isGoogleMap
                        ? "Google Maps"
                        : "OpenStreetMap"}
                </span>
              </div>
            </div>

            <div className="w-full h-[500px] lg:h-[620px] relative rounded-2xl overflow-hidden border border-[var(--surface-border)] bg-[var(--surface)]">
              {!isGoogleMap && (
                <div
                  ref={leafletMapElementRef}
                  className="absolute inset-0 h-full w-full"
                  aria-label="OpenStreetMap place selector"
                />
              )}

              {isGoogleMap && (
                <div
                  ref={googleMapElementRef}
                  className="absolute inset-0 h-full w-full"
                  aria-label="Google Maps place selector"
                />
              )}

              {mapLoading && (
                <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
                  <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
                  <p className="text-sm font-extrabold text-[var(--text-primary)]">
                    Loading {isGoogleMap ? "Google Maps" : "OpenStreetMap"}...
                  </p>
                </div>
              )}

              {!mapLoading && mapError && (
                <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
                  <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-xl">
                      ⚠️
                    </div>
                    <p className="mt-3 text-sm font-extrabold text-red-700 m-0">
                      Map could not be loaded
                    </p>
                    <p className="mt-2 text-xs font-semibold text-red-600 m-0">
                      {mapError}
                    </p>

                    {isGoogleMap && (
                      <div className="mt-4 rounded-xl bg-amber-50 p-3 text-left">
                        <p className="text-[11px] font-extrabold uppercase tracking-wide text-amber-700 m-0">
                          Required Google Cloud settings
                        </p>
                        <p className="mt-1.5 text-[11px] font-semibold leading-5 text-amber-700 m-0">
                          Add <strong>{getCurrentOrigin()}/*</strong> under API
                          key website restrictions. Also enable Maps JavaScript
                          API, Places API and Geocoding API.
                        </p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="mt-4 inline-flex h-10 items-center justify-center rounded-xl bg-red-600 px-5 text-[13px] font-extrabold text-white transition hover:bg-red-700"
                    >
                      Reload Page
                    </button>
                  </div>
                </div>
              )}

              {!mapLoading && !mapError && (
                <div className="pointer-events-none absolute bottom-4 left-4 right-4 z-[500] flex justify-center">
                  <div className="rounded-xl border border-white/40 bg-black/70 px-4 py-2 text-center text-[12px] font-bold text-white shadow-lg backdrop-blur-md">
                    Click anywhere on the map to select a place
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Leaflet custom styles (same as RouteMaster) */}
        <style>{`
          .custom-map-pin-wrapper {
            background: transparent !important;
            border: none !important;
          }

          .leaflet-control-geocoder {
            border: 1px solid var(--surface-border) !important;
            background: var(--card-bg) !important;
            border-radius: 14px !important;
            overflow: hidden !important;
            box-shadow: 0 10px 30px rgba(0,0,0,0.35) !important;
          }

          .leaflet-control-geocoder-icon {
            width: 44px !important;
            height: 44px !important;
            background: var(--btn-bg) !important;
            border-radius: 14px !important;
            position: relative !important;
            background-image: none !important;
          }

          .leaflet-control-geocoder-icon::before {
            content: "";
            width: 18px;
            height: 18px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-repeat: no-repeat;
            background-position: center;
            background-size: contain;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 24 24'%3E%3Cpath d='M10 2a8 8 0 105.293 14.293l4.707 4.707 1.414-1.414-4.707-4.707A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z'/%3E%3C/svg%3E");
          }

          .leaflet-control-geocoder-expanded {
            width: 360px !important;
            display: flex !important;
            align-items: center !important;
            border-radius: 14px !important;
          }

          .leaflet-container {
            width: 100% !important;
            height: 100% !important;
            min-height: 420px !important;
            z-index: 1;
          }

          .leaflet-control-geocoder-expanded .leaflet-control-geocoder-icon {
            border-radius: 14px 0 0 14px !important;
            flex: 0 0 44px !important;
          }

          .leaflet-control-geocoder-form { flex: 1 !important; }

          .leaflet-control-geocoder-form input {
            width: 100% !important;
            height: 44px !important;
            background: var(--card-bg) !important;
            color: var(--text-primary) !important;
            border: none !important;
            outline: none !important;
            padding: 0 14px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
          }

          .leaflet-control-geocoder-form input::placeholder {
            color: var(--text-faint) !important;
          }

          .leaflet-control-geocoder-alternatives {
            background: var(--card-bg) !important;
            border: 1px solid var(--surface-border) !important;
            border-radius: 12px !important;
            margin-top: 6px !important;
            box-shadow: 0 14px 35px rgba(0,0,0,0.4) !important;
            overflow: hidden !important;
          }

          .leaflet-control-geocoder-alternatives li {
            color: var(--text-primary) !important;
            padding: 10px 12px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            border-bottom: 1px solid var(--surface-border) !important;
          }

          .leaflet-control-geocoder-alternatives li:hover {
            background: var(--accent-faint) !important;
          }

          .leaflet-control-zoom a {
            color: #111827 !important;
            font-weight: 800 !important;
          }
        `}</style>
      </div>
    </Layout>
  );
}